import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Save } from 'lucide-react';

const settingsKeys = [
  { key: 'hero_image', label: 'Hero Banner Image URL' },
  { key: 'flash_sale_image', label: 'Flash Sale Banner Image URL' },
  { key: 'promo_banner', label: 'Promo Banner Image URL' },
  { key: 'store_phone', label: 'Store Phone Number' },
  { key: 'store_address', label: 'Store Address' },
];

const AdminSettings = () => {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});

  const { data: settings = [] } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: async () => {
      const { data, error } = await supabase.from('homepage_settings').select('*');
      if (error) throw error;
      // Populate state
      const vals: Record<string, string> = {};
      data?.forEach((s: any) => { vals[s.key] = s.value || s.image_url || ''; });
      setValues(vals);
      return data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      for (const sk of settingsKeys) {
        const val = values[sk.key] || '';
        const existing = settings.find((s: any) => s.key === sk.key);
        if (existing) {
          await supabase.from('homepage_settings').update({
            value: val,
            image_url: sk.key.includes('image') || sk.key.includes('banner') ? val : null,
          }).eq('id', (existing as any).id);
        } else {
          await supabase.from('homepage_settings').insert({
            key: sk.key,
            value: val,
            image_url: sk.key.includes('image') || sk.key.includes('banner') ? val : null,
          });
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      toast({ title: 'Settings saved' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold mb-6">Homepage Settings</h1>
      <Card>
        <CardHeader><CardTitle className="font-heading text-lg">Customize Homepage</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {settingsKeys.map(sk => (
            <div key={sk.key}>
              <Label>{sk.label}</Label>
              <Input
                value={values[sk.key] || ''}
                onChange={e => setValues(prev => ({ ...prev, [sk.key]: e.target.value }))}
                placeholder={sk.label}
              />
            </div>
          ))}
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending} className="gap-2">
            <Save className="h-4 w-4" /> {saveMutation.isPending ? 'Saving...' : 'Save Settings'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSettings;
